import { useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { StaffConversionMetrics } from 'app/types/qualified-leads'
import { gqlQuery } from 'app/lib/graphqlClient'

const QUALIFIED_LEAD_COUNTS_QUERY = graphql(`
  query QualifiedLeadCounts(
    $dateRange: DateRangeInput!
    $locationIds: [String!]
    $channel: String
    $integrationStatus: String
    $filters: String
    $platform: String
  ) {
    qualifiedLeadCounts(
      dateRange: $dateRange
      locationIds: $locationIds
      channel: $channel
      integrationStatus: $integrationStatus
      filters: $filters
      platform: $platform
    ) {
      total
      qualified
      disqualified
      staffConversionMetrics {
        userId
        userName
        leads
        qualified
        disqualified
      }
    }
  }
`)

/**
 * Fetches legacy staff conversion metrics from /api/metrics/conversion/qualified-leads.
 * Groups data by contact owner (contact-based).
 *
 * @param enabled - When false, skips the API call entirely. Used by the Marketing
 *   dashboard to avoid a wasted request when staff-dashboard-v2 is ON, since that path uses
 *   the scorecards endpoint instead. Defaults to true for backward compatibility.
 */
export default function useStaffMetrics(
  fromDate: string,
  toDate: string,
  locationIds: string[],
  channel: string,
  query: string,
  integrationStatus?: string,
  filters?: string,
  platform?: string | null,
  enabled = true,
): [StaffConversionMetrics[] | undefined, boolean] {
  const [staffRows, setStaffRows] = useState<StaffConversionMetrics[]>()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!enabled || !fromDate || !toDate) return
    const dateRange = { fromDate, toDate }
    setIsLoading(true)
    gqlQuery<{
      qualifiedLeadCounts: {
        total: number
        qualified: number
        disqualified: number
        staffConversionMetrics: StaffConversionMetrics[]
      }
    }>(QUALIFIED_LEAD_COUNTS_QUERY, {
      dateRange,
      locationIds: locationIds.length ? locationIds : undefined,
      channel: channel || undefined,
      integrationStatus: integrationStatus || undefined,
      filters: filters || undefined,
      platform: platform || undefined,
    })
      .then((result) => {
        if (result.data) setStaffRows(result.data.qualifiedLeadCounts.staffConversionMetrics)
      })
      .finally(() => setIsLoading(false))
  }, [fromDate, toDate, channel, locationIds, query, integrationStatus, filters, platform, enabled])

  return [staffRows, isLoading]
}
